/** @jsxFrag React.Fragment */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { debounce } from 'lodash';

import { TextField } from '@coursera/cds-core';
import { AnswerWrongIcon, ChevronDownIcon, ChevronUpIcon, CloseIcon } from '@coursera/cds-icons';

import { CoachIconButton } from 'bundles/ai-coach-platform/components/building-blocks';
import CoachSkipLink from 'bundles/ai-coach-platform/components/patterns/chat/common/CoachSkipLink';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  searchContainer: css`
    width: 100%;
    display: flex;
    align-items: center;
    border-radius: var(--cds-border-radius-100);
    gap: var(--cds-spacing-50);
    background-color: var(--cds-color-neutral-background-primary-weak);
    position: relative;
  `,
  searchField: css`
    /* CDS overrides for Coach styling */
    .cds-input-root {
      height: 36px;
      max-height: 36px;
      margin: 0;
      background-color: transparent;
      border-top-left-radius: var(--cds-border-radius-100) !important;
      border-bottom-left-radius: var(--cds-border-radius-100) !important;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;

      &:hover {
        .cds-input-input::placeholder {
          color: var(--cds-color-neutral-stroke-primary) !important;
        }
      }
    }

    fieldset {
      border: none !important;
    }

    input {
      max-height: 36px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      line-height: 1;
    }

    .cds-input-input {
      padding: var(--cds-spacing-100) var(--cds-spacing-100) var(--cds-spacing-100) var(--cds-spacing-200);
    }

    .cds-input-focused {
      &::before {
        display: none;
      }

      &:hover {
        background: var(--cds-color-neutral-background-primary-weak) !important;
      }

      .cds-input-input::placeholder {
        color: var(--cds-color-neutral-stroke-primary) !important;
      }
    }

    .cds-inputAdornment-end {
      padding: 0;
    }
  `,
  searchActions: css`
    height: 36px;
    display: flex;
    align-items: center;
    background-color: transparent;
    border-left: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-top-right-radius: var(--cds-border-radius-100);
    border-bottom-right-radius: var(--cds-border-radius-100);

    button:hover {
      background-color: var(--cds-color-emphasis-neutral-background-weak);
    }
  `,
  actionButton: css`
    svg path {
      color: var(--cds-color-neutral-primary);
    }
  `,
  results: css`
    padding: var(--cds-spacing-50);
    color: var(--cds-color-neutral-primary-weak);
  `,
  skipLink: css`
    position: absolute;
    top: 40px;
    z-index: 1;
  `,
};

export type Props = {
  /**
   * placeholder text for the search bar input
   */
  placeholder?: string;
  /**
   * callback for every value change
   */
  onChange?: (value: string) => void;
  /**
   * callback for the debounced update to the search value,
   * use this for the actual search
   */
  onSubmit: (value: string) => void;
  /**
   * callback when search is cleared
   */
  onClear?: () => void;
  /**
   * whether the search bar is disabled
   */
  disabled?: boolean;
  /**
   * callback when the search bar is closed
   */
  onClose?: () => void;

  /**
   * search results metadata
   */
  results?: {
    total: number;
    current: number;
  };
  /**
   * callback when the user navigates up a search result
   */
  onNavigateUp?: (current: number) => void;
  /**
   * callback when the user navigates down
   */
  onNavigateDown?: (current: number) => void;

  /**
   * callback when the user clicks the search skip link
   */
  onSkipLinkClick?: () => void;

  /**
   * ref to the target element for the skip link
   */
  skipLinkTargetRef?: React.RefObject<HTMLElement>;

  'aria-label'?: string;
  'data-testid'?: string;
};

export const SearchBar = (props: Props): React.ReactElement => {
  const {
    placeholder = _t('Search'),
    onChange,
    onSubmit,
    onClear,
    disabled = false,
    'data-testid': dataTestId = 'coach-search-bar',
    'aria-label': ariaLabel = _t('#{coachLabel} chat search', { coachLabel: 'Coach' }),
    onNavigateUp,
    onNavigateDown,
    onClose,
    results = { total: 0, current: 0 },
    onSkipLinkClick,
    skipLinkTargetRef,
  } = props;

  const [value, setValue] = useState('');

  const inputRef = React.useRef<HTMLInputElement>(null);
  const navigateDownRef = React.useRef<HTMLButtonElement>(null);
  const submitDebounce = React.useMemo(() => debounce(onSubmit, 400), [onSubmit]);

  const resultsCurrent = results.current;

  useEffect(() => {
    return () => {
      submitDebounce.cancel();
    };
  }, [submitDebounce]);

  useEffect(() => {
    if (value.trim()) {
      submitDebounce(value);
    }
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setValue(newValue);
    onChange?.(newValue);

    if (newValue === '') {
      submitDebounce.cancel();
      onClear?.();
    }
  };

  const handleClear = () => {
    submitDebounce.cancel();
    setValue('');
    onClear?.();
    inputRef.current?.focus();
  };

  const handleClose = () => {
    onClose?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && results.total > 1) {
      event.preventDefault();

      if (event.shiftKey) {
        // reverse navigation
        onNavigateUp?.(resultsCurrent);
      } else {
        onNavigateDown?.(resultsCurrent);
      }
    }
  };

  const handleNavigateDown = useCallback<() => void>(() => {
    // cycle back to the first result
    const newCurrent = resultsCurrent === results.total ? 1 : resultsCurrent + 1;
    onNavigateDown?.(newCurrent);
  }, [onNavigateDown, results.total, resultsCurrent]);

  const handleNavigateUp = useCallback<() => void>(() => {
    // cycle back to the last result
    const newCurrent = resultsCurrent === 1 ? results.total : resultsCurrent - 1;
    onNavigateUp?.(newCurrent);
  }, [onNavigateUp, results.total, resultsCurrent]);

  return (
    <div className="coach-search-bar" css={styles.searchContainer}>
      <TextField
        autoFocus
        css={styles.searchField}
        inputRef={inputRef}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        fullWidth
        inputProps={{
          'data-testid': dataTestId,
          role: 'searchbox',
          autoComplete: 'off',
          onKeyDown: handleKeyDown,
        }}
        suffix={
          <>
            {!!value && (
              <CoachIconButton
                size="small"
                css={styles.actionButton}
                icon={<AnswerWrongIcon />}
                tooltip={_t('Clear search')}
                aria-label={_t('Clear search')}
                onClick={handleClear}
              />
            )}

            <div css={styles.results}>
              {/* 1-based index */}
              {results.total > 0 ? resultsCurrent + 1 : resultsCurrent}/{results.total}
            </div>
          </>
        }
      />
      {!!value && (
        <CoachSkipLink
          label="Go to current match"
          targetRef={skipLinkTargetRef ?? navigateDownRef}
          onClick={onSkipLinkClick}
          visibleCss={styles.skipLink}
        />
      )}

      <div css={styles.searchActions}>
        <CoachIconButton
          size="small"
          css={styles.actionButton}
          icon={<ChevronUpIcon />}
          tooltip={_t('Previous match')}
          aria-label={_t('Go to previous match')}
          onClick={handleNavigateUp}
          disabled={results?.total < 2}
          data-testid="search-navigate-up"
        />
        <CoachIconButton
          size="small"
          css={styles.actionButton}
          icon={<ChevronDownIcon />}
          tooltip={_t('Next match')}
          aria-label={_t('Go to next match')}
          onClick={handleNavigateDown}
          disabled={results?.total < 2}
          data-testid="search-navigate-down"
          ref={navigateDownRef}
        />
        <CoachIconButton
          size="small"
          css={styles.actionButton}
          icon={<CloseIcon />}
          tooltip={_t('Close search')}
          aria-label={_t('Close find-in-chat search')}
          onClick={handleClose}
          data-testid="search-close"
        />
      </div>
    </div>
  );
};

export default SearchBar;
